import React, { useEffect, useState } from "react";
import { getTransactionHistory, getInvoicePreview } from "../../../services/payment/api";
import "./TransactionHistory.css";
import InvoiceModal from "../../Payment/Invoices/InvoiceModal";

const TransactionHistory = ({ clientId }) => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [invoiceHtml, setInvoiceHtml] = useState("");
    
    // Toast Alert State Engine
    const [toast, setToast] = useState({ show: false, message: "", type: "" });

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 4000);
    };

    useEffect(() => {
        fetchTransactions();
    }, [clientId]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            const data = await getTransactionHistory(clientId);
            setTransactions(data || []);
        } catch (error) {
            console.error(error);
            showToast("Failed to compile statement history data.", "error");
        } finally {
            setLoading(false);
        }
    };

    // FIXED: Now accepts target id and type parameters dynamically
    const handleOpenInvoice = async (id, type) => {
        if (!id) {
            showToast("No explicit invoice reference mapped to this transaction context.", "error");
            return;
        }
        try {
            showToast(`Opening ${type.toLowerCase()} statement context...`, "info");
            // Calls the api wrapper passing both parameters to resolve the endpoint query strings
            const htmlContent = await getInvoicePreview(id, type);
            setInvoiceHtml(htmlContent);
            setModalOpen(true);
        } catch (err) {
            showToast("Error processing data sheet visualization layout.", "error");
        }
    };

    if (loading) {
        return <div className="loading">Loading records matrix...</div>;
    }

    return (
        <div className="transaction-container">
            {toast.show && (
                <div className={`custom-toast toast-${toast.type}`}>
                    {toast.message}
                </div>
            )}

            <h2>Transaction History</h2>

            <table className="transaction-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Credits</th>
                        <th>Balance</th>
                        <th>Reference ID</th>
                        <th>Status</th>
                        <th>Invoices</th>
                    </tr>
                </thead>
                <tbody>
                {transactions.length === 0 ? (
                    <tr>
                        <td colSpan="8">No Transactions Found</td>
                    </tr>
                ) : (
                    transactions.map((transaction) => (
                        <tr key={transaction.transactionId}>
                            <td>{new Date(transaction.createdAt).toLocaleString()}</td>
                            <td>
                                <span className={`transaction-type ${transaction.transactionType === "RECHARGE" ? "recharge" : "deduction"}`}>
                                    {transaction.transactionType}
                                </span>
                            </td>
                            <td>{transaction.description}</td>
                            <td>
                                {transaction.transactionType === "RECHARGE"
                                    ? `+₹${transaction.credits}`
                                    : `-₹${transaction.credits}`}
                            </td>
                            <td>₹{transaction.balanceAfterTransaction}</td>
                            <td>{transaction.referenceId || "-"}</td>
                            <td>
                                <span className={`transaction-status ${transaction.status === "SUCCESS" ? "success" : "failed"}`}>
                                    {transaction.status}
                                </span>
                            </td>
                            <td>
                                {/* FIXED: Kept button visible for both transaction frameworks */}
                                <button 
                                  className="view-invoice-btn" 
                                  onClick={() => {
                                      const isRecharge = transaction.transactionType === "RECHARGE";
                                      const targetId = isRecharge ? transaction.rechargeId : transaction.transactionId;
                                      handleOpenInvoice(targetId, transaction.transactionType);
                                  }}
                                  // Prevents clicking legacy recharge items missing a rechargeId mapping
                                  disabled={transaction.transactionType === "RECHARGE" && !transaction.rechargeId}
                                >
                                    View
                                </button>
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
            </table>

            <InvoiceModal 
                isOpen={modalOpen} 
                htmlContent={invoiceHtml} 
                onClose={() => setModalOpen(false)} 
            />
        </div>
    );
};

export default TransactionHistory;